import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import toast, { Toaster } from 'react-hot-toast';
import { formatPhoneInput, otpErrorMessage, PHONE_FORMAT_ERROR, PHONE_HELP_TEXT, toE164 } from '../utils/phone';
import { ArrowLeftIcon, PlayIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { StarIcon } from '@heroicons/react/24/outline';

type Step = 'auth' | 'compose' | 'sent' | 'registered';

const MessagePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Registration context (fan just followed the creator from bio page)
  const isRegistered = searchParams.get('registered') === '1';
  const registrationCoupon = searchParams.get('coupon') || '';

  const [isCreditUser, setIsCreditUser] = useState(searchParams.get('campaign') === 'credit24h');
  const [creditExpiresAt, setCreditExpiresAt] = useState<Date | null>(null);

  const [talent, setTalent] = useState<any>(null);
  const [bioSettings, setBioSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<Step>(isRegistered ? 'registered' : 'auth');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [recentShoutouts, setRecentShoutouts] = useState<any[]>([]);
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);
  const [occasionIndex, setOccasionIndex] = useState(0);
  const [randomReview, setRandomReview] = useState<any>(null);

  // 24-hour countdown for the registration coupon or credit expiration
  const couponExpiresAt = useRef<Date>(new Date(Date.now() + 24 * 60 * 60 * 1000));
  const [countdown, setCountdown] = useState('');
  useEffect(() => {
    if (!isRegistered) return;
    const tick = () => {
      const targetTime = creditExpiresAt || couponExpiresAt.current;
      const diff = targetTime.getTime() - Date.now();
      if (diff <= 0) { setCountdown('expired'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(`${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [isRegistered, creditExpiresAt]);

  // Auth
  const [email, setEmail] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [emailValid, setEmailValid] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isVerified, setIsVerified] = useState(false);

  // OTP
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpPhone, setOtpPhone] = useState('');
  const [otpPhoneHint, setOtpPhoneHint] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpNeedsPhone, setOtpNeedsPhone] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!username) return;
      let tp: any = null;
      const { data: byUsername } = await supabase
        .from('talent_profiles')
        .select('id, user_id, username, full_name, temp_full_name, temp_avatar_url, support_enabled, fan_shoutouts_enabled, fulfilled_orders, created_at')
        .eq('username', username).single();
      if (byUsername) tp = byUsername;
      else if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(username)) {
        const { data: byId } = await supabase
          .from('talent_profiles')
          .select('id, user_id, username, full_name, temp_full_name, temp_avatar_url, support_enabled, fan_shoutouts_enabled, fulfilled_orders, created_at')
          .eq('id', username).single();
        tp = byId;
      }
      if (!tp || (!tp.fan_shoutouts_enabled && !tp.support_enabled && !isRegistered)) { navigate(`/${username}`); return; }
      setTalent(tp);
      const { data: bs } = await supabase.from('bio_settings').select('*').eq('talent_id', tp.id).single();
      setBioSettings(bs);

      // SO-005: recipient_name is anon-forbidden; RPC returns first name only.
      supabase
        .rpc('get_talent_public_shoutouts', { p_talent_id: tp.id, p_limit: 6 })
        .then(({ data }) => {
          if (data) setRecentShoutouts((data as any[]).map(s => ({
            id: s.id,
            video_url: s.video_url,
            occasion: s.occasion,
            created_at: s.created_at,
            recipient_name: s.recipient_first_name || 'a fan',
          })));
        });

      supabase
        .from('reviews')
        .select('id, rating, comment, created_at, users!inner(full_name)')
        .eq('talent_id', tp.id)
        .gte('rating', 4)
        .not('comment', 'is', null)
        .limit(10)
        .then(({ data }) => {
          if (data && data.length > 0) {
            setRandomReview(data[Math.floor(Math.random() * data.length)]);
          }
        });

      const savedEmail = localStorage.getItem('bio_subscriber_email');
      const verified = localStorage.getItem('bio_account_verified') === 'true';
      if (savedEmail && verified) {
        setEmail(savedEmail);
        setEmailValid(true);
        setIsVerified(true);
        if (!isRegistered) setStep('compose');

        // Check for active credit grants in the background. anon can't
        // read/filter users by email post-incident, so resolve id via RPC.
        supabase
          .rpc('lookup_user_by_email', { p_email: savedEmail.trim().toLowerCase() })
          .then(({ data: usrRows }) => {
            const usr = Array.isArray(usrRows) ? usrRows[0] : (usrRows as any);
            if (usr?.id) {
              // credit_grants is locked down (RLS + no anon grants) after the
              // 2026-07-09 incident; read the soonest active-grant expiry via the
              // SECURITY DEFINER RPC instead of querying the table directly.
              supabase
                .rpc('bio_active_credit_grant', { p_user_id: usr.id })
                .then(({ data: expiresAt }) => {
                  if (expiresAt) {
                    setIsCreditUser(true);
                    setCreditExpiresAt(new Date(expiresAt as string));
                  } else {
                    setIsCreditUser(false);
                  }
                });
            } else {
              setIsCreditUser(false);
            }
          });
      } else if (isRegistered) {
        // If they land on registered=1 and are NOT logged in, they must have come from the email campaign
        setIsCreditUser(true);
      }
      setLoading(false);
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, navigate]);

  const displayName = bioSettings?.display_name || talent?.full_name || talent?.temp_full_name || 'Creator';
  const firstName = displayName.startsWith('The ') || displayName.startsWith('Chicks') ? displayName : displayName.split(' ')[0];
  const profileImage = bioSettings?.profile_image_url || talent?.temp_avatar_url;

  const shoutoutOccasions = [
    'the unforgettable birthday gift. 🎂',
    'the roast they\'ll never forget. 🔥',
    'a pep talk that actually hits. 💪',
    'the announcement they\'ll replay forever. 📣',
    'a thank you that means everything. 🙏',
    'the holiday surprise they didn\'t see coming. 🎁',
    'a graduation message worth framing. 🎓',
    'the ultimate fan moment. ⭐',
    'a get well message from the heart. ❤️',
    'wedding wishes they\'ll always remember. 💍',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setOccasionIndex(prev => (prev + 1) % shoutoutOccasions.length);
    }, 3000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const gradStart = bioSettings?.gradient_start || '#0a0a0a';
  const gradEnd = bioSettings?.gradient_end || '#1a1a2e';

  const [emailLoading, setEmailLoading] = useState(false);

  const handleEmailSubmit = async () => {
    const v = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
    setEmailValid(v);
    if (!v) return;
    setEmailLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: email.toLowerCase().trim(), checkEmailOnly: true },
      });

      const resp = data || (error ? (() => { try { return JSON.parse((error as any)?.message || '{}'); } catch { return {}; } })() : {});

      if (resp.rateLimited) {
        toast.error('Please wait 60 seconds before requesting another code.');
        if (resp.phoneHint) {
          setOtpPhoneHint(resp.phoneHint);
          setOtpSent(true);
          setOtpNeedsPhone(false);
          setShowOtpModal(true);
        }
        return;
      }

      // The phone on file couldn't be texted (e.g. a country we can't text
      // yet): show the real reason.
      if (error || data?.error || data?.success === false) {
        toast.error(await otpErrorMessage(error, data));
        return;
      }

      if (data?.sentToExistingPhone) {
        setOtpPhoneHint(data.phoneHint || '');
        setOtpPhone(data.phone || '');
        setOtpSent(true);
        setOtpNeedsPhone(false);
        setShowOtpModal(true);
      } else if (data?.needsPhone || (!data?.sentToExistingPhone && !error)) {
        setOtpNeedsPhone(true);
        setShowOtpModal(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleOtpSend = async () => {
    if (!otpPhone.trim()) { toast.error('Enter your phone number'); return; }
    const e164 = toE164(otpPhone);
    if (!e164) { toast.error(PHONE_FORMAT_ERROR); return; }
    setOtpLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: email.toLowerCase().trim(), phone: e164 },
      });
      if (error || data?.error || data?.success === false) { toast.error(await otpErrorMessage(error, data)); return; }
      setOtpPhoneHint(data?.phoneHint || e164.slice(-4));
      setOtpSent(true);
      toast.success('Code sent!');
    } catch { toast.error('Failed to send code'); }
    finally { setOtpLoading(false); }
  };

  const handleOtpVerify = async () => {
    if (otpCode.length < 4) { toast.error('Enter the code'); return; }
    setOtpLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-registration-otp', {
        body: { email: email.toLowerCase().trim(), phone: toE164(otpPhone) || undefined, code: otpCode },
      });
      if (error || data?.error || data?.success === false) { toast.error(await otpErrorMessage(error, data, 'Invalid code')); return; }
      toast.success('Verified!');
      localStorage.setItem('bio_subscriber_email', email.toLowerCase().trim());
      localStorage.setItem('bio_account_verified', 'true');
      setIsVerified(true);
      setShowOtpModal(false);
      setStep('compose');
    } catch { toast.error('Invalid code'); }
    finally { setOtpLoading(false); }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) { toast.error('Please write a message'); return; }
    setSending(true);
    try {
      await supabase.functions.invoke('lunarpay-api', {
        body: {
          action: 'insert-donation-message',
          talentId: talent.id,
          donorEmail: email.toLowerCase(),
          donorName: email.split('@')[0],
          message: message.trim(),
          amount: 0,
          isMonthly: false,
          orderId: null,
        },
      });

      // Ensure user becomes a fan/follower. anon can't read/filter users by
      // email post-incident, so resolve the id via the definer RPC.
      const { data: existingUserRows } = await supabase.rpc('lookup_user_by_email', { p_email: email.trim().toLowerCase() });
      const existingUser = Array.isArray(existingUserRows) ? existingUserRows[0] : (existingUserRows as any);
      if (existingUser?.id) {
        await supabase.from('talent_followers').upsert({
          user_id: existingUser.id,
          talent_id: talent.id,
        }, { onConflict: 'user_id,talent_id', ignoreDuplicates: true });

        supabase.functions.invoke('capture-lead', {
          body: { email: email.toLowerCase(), source: 'bio_page', talent_slug: talent.username || talent.id },
        }).catch(() => {});
      }

      toast.success('Message sent!');
      setStep('sent');
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(to bottom, ${gradStart}, ${gradEnd})` }}>
      <Toaster position="top-center" />
      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate(`/${username}`)} className="flex items-center gap-2 text-gray-400 hover:text-white">
            <ArrowLeftIcon className="h-4 w-4" /><span className="text-sm">Back</span>
          </button>
          <img src="/shoutoutwhite.png" alt="ShoutOut" className="h-5 opacity-60" />
        </div>

        {/* Profile */}
        <div className="text-center mb-6">
          {profileImage && <img src={profileImage} alt="" className="w-20 h-20 rounded-full mx-auto mb-3 object-cover border-2 border-white/20" />}
          {step !== 'sent' && step !== 'registered' && (
            <>
              <h1 className="text-2xl font-bold text-white">ShoutOut {displayName}</h1>
              <p className="text-gray-400 text-sm mt-1">Send some encouragement, thanks, or feedback to {firstName}</p>
            </>
          )}
          {step === 'registered' && (
            <h1 className="text-2xl font-bold text-white">{displayName}</h1>
          )}
        </div>

        {/* Step: Auth */}
        {step === 'auth' && (
          <div className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleEmailSubmit(); }}
              placeholder="Enter your email to continue"
              className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40"
            />
            <button
              onClick={handleEmailSubmit}
              disabled={emailLoading || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)}
              className="w-full py-3.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
            >
              {emailLoading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Continue'}
            </button>
            <p className="text-gray-500 text-xs text-center">We'll verify your identity with a quick code</p>
          </div>
        )}

        {/* Step: Compose */}
        {step === 'compose' && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">Messaging as <span className="text-white font-medium">{email}</span></p>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={`Write a message to ${firstName}...`}
              maxLength={500}
              rows={5}
              className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40 resize-none text-sm"
              autoFocus
            />
            <div className="flex items-center justify-between">
              <p className="text-gray-500 text-xs">These messages are public.</p>
              <p className="text-gray-500 text-xs">{message.length}/500</p>
            </div>
            <button
              onClick={handleSendMessage}
              disabled={sending || !message.trim()}
              className="w-full py-3.5 rounded-xl font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
            >
              {sending ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Send Message'}
            </button>
          </div>
        )}

        {/* Step: Registered — post fan-follow CTAs */}
        {step === 'registered' && (() => {
          const shoutoutUrl = `/${talent?.username || username}/shoutout${registrationCoupon ? `?coupon=${encodeURIComponent(registrationCoupon)}` : ''}`;
          const isFirst10PromoActive = 
            !!talent && 
            (talent.fulfilled_orders || 0) < 10 && 
            (talent.created_at ? (new Date().getTime() - new Date(talent.created_at).getTime()) / (1000 * 60 * 60 * 24) <= 40 : false);
          return (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="text-4xl mb-2">{isCreditUser ? '💵' : '💚'}</div>
                <h2 className="text-xl font-bold text-white">
                  {isCreditUser ? '$10 Credit Added!' : `You're connected to ${firstName}!`}
                </h2>
                {isCreditUser ? (
                  countdown && countdown !== 'expired' ? (
                    <p className="text-gray-400 text-sm mt-1">
                      Credit expires in: <span className="text-amber-400 font-extrabold tabular-nums">{countdown}</span>
                    </p>
                  ) : (
                    <p className="text-red-400 text-sm mt-1 font-semibold">Credit expired</p>
                  )
                ) : (
                  <p className="text-gray-400 text-sm mt-1">{firstName} will see you in their fan list</p>
                )}
              </div>

              {/* ShoutOut CTA — first, with new title + 25% off countdown */}
              {true && (
                <a href={shoutoutUrl} className="block">
                  <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl overflow-hidden border border-blue-500/30 hover:border-blue-500/50 transition-all duration-300 hover:scale-[1.02]">
                    <div className="flex items-stretch">
                      <div className="w-20 flex-shrink-0">
                        {profileImage ? (
                          <img src={profileImage} alt={displayName} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                            {displayName[0]}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 p-4 flex flex-col justify-center">
                        <p className="text-white text-sm font-bold leading-snug mb-1.5">
                          Get a Personalized Video From {firstName}.
                        </p>
                        {!isCreditUser && countdown && countdown !== 'expired' && (
                          <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/15 border border-amber-500/30 text-amber-400 mb-1.5 w-fit">
                            <span>New Fan Discount: {isFirst10PromoActive ? '50%' : '25%'} off for</span>
                            <span className="tabular-nums font-extrabold text-amber-300">{countdown}</span>
                          </div>
                        )}
                        {randomReview ? (
                          <div className="mt-1 pt-1.5 border-t border-white/10">
                            <div className="flex items-center gap-1 mb-0.5">
                              {[...Array(5)].map((_, i) => (
                                i < randomReview.rating ? (
                                  <StarSolidIcon key={i} className="h-3 w-3 text-yellow-400" />
                                ) : (
                                  <StarIcon key={i} className="h-3 w-3 text-gray-600" />
                                )
                              ))}
                            </div>
                            {randomReview.comment && (
                              <p className="text-gray-300 text-xs line-clamp-1">
                                "{randomReview.comment.split('\n')[0]}"
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-400 text-[10px]">From {displayName}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </a>
              )}

              {/* Support CTA — second */}
              <a href={`/${talent?.username || username}/support?skipMessage=true`} className="block">
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl overflow-hidden border border-green-500/30 hover:border-green-500/50 transition-all duration-300 hover:scale-[1.02] p-5">
                  <h3 className="text-white font-semibold text-base mb-2">Support {firstName} With a Donation</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <span className="text-green-400">✓</span> Get a guaranteed response from {firstName}
                    </li>
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <span className="text-green-400">✓</span> Your message shown on {firstName}'s page
                    </li>
                    <li className="flex flex-col gap-1 text-gray-300 text-sm pt-1">
                      <div className="ml-5 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/5 text-gray-400 border border-white/10 w-fit">
                        Earn 1 point per $1 donated and show up higher on {firstName}'s Top Fans
                      </div>
                    </li>
                  </ul>
                </div>
              </a>

              {/* Recent ShoutOuts Video Feed */}
              {recentShoutouts.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-white font-semibold text-sm mb-3">ShoutOuts {firstName} has sent other fans.</h3>
                  <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory" style={{ scrollbarWidth: 'none' }}>
                    {recentShoutouts.map(shoutout => (
                      <div key={shoutout.id} className="flex-shrink-0 w-40 snap-start">
                        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                          <div className="aspect-[9/16] relative bg-black">
                            <video
                              src={`${shoutout.video_url}#t=0.5`}
                              className="w-full h-full object-cover"
                              playsInline
                              preload="auto"
                              onClick={(e) => {
                                const vid = e.currentTarget;
                                if (vid.paused) { vid.muted = false; vid.play(); setPlayingVideoId(shoutout.id); }
                                else { vid.pause(); setPlayingVideoId(null); }
                              }}
                              onEnded={() => setPlayingVideoId(null)}
                            />
                            {playingVideoId !== shoutout.id && (
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-10 h-10 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                                  <PlayIcon className="h-5 w-5 text-white ml-0.5" />
                                </div>
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 p-2 pointer-events-none">
                              <p className="text-white text-xs font-medium truncate">For {shoutout.recipient_name || 'a fan'}</p>
                              <p className="text-gray-400 text-[10px]">{shoutout.occasion || 'ShoutOut'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Step: Sent — CTAs */}
        {step === 'sent' && (
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="text-4xl mb-2">💚</div>
              <h2 className="text-xl font-bold text-white">Message Sent!</h2>
              <p className="text-gray-400 text-sm mt-1">{firstName} will see your message</p>
            </div>

            {/* ShoutOut CTA — first */}
            <a
              href={`/${talent?.username || username}/shoutout`}
              className="block"
            >
              <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl overflow-hidden border border-blue-500/30 hover:border-blue-500/50 transition-all duration-300 hover:scale-[1.02]">
                <div className="flex items-stretch">
                  <div className="w-20 flex-shrink-0">
                    {profileImage ? (
                      <img src={profileImage} alt={displayName} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                        {displayName[0]}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 p-4 flex flex-col justify-center">
                    <p className="text-blue-400 text-sm font-semibold mb-1">
                      Get a personalized video ShoutOut for...
                    </p>
                    <div className="h-5 overflow-hidden relative">
                      {shoutoutOccasions.map((phrase, i) => (
                        <div
                          key={i}
                          className="absolute inset-x-0 transition-all duration-500 ease-in-out"
                          style={{
                            transform: i === occasionIndex ? 'translateY(0)' : i === (occasionIndex - 1 + shoutoutOccasions.length) % shoutoutOccasions.length ? 'translateY(-100%)' : 'translateY(100%)',
                            opacity: i === occasionIndex ? 1 : 0,
                          }}
                        >
                          <p className="text-white text-sm font-semibold">{phrase}</p>
                        </div>
                      ))}
                    </div>
                    {randomReview ? (
                      <div className="mt-2 pt-2 border-t border-white/10">
                        <div className="flex items-center gap-1 mb-0.5">
                          {[...Array(5)].map((_, i) => (
                            i < randomReview.rating ? (
                              <StarSolidIcon key={i} className="h-3 w-3 text-yellow-400" />
                            ) : (
                              <StarIcon key={i} className="h-3 w-3 text-gray-600" />
                            )
                          ))}
                        </div>
                        {randomReview.comment && (
                          <p className="text-gray-300 text-xs line-clamp-1">
                            "{randomReview.comment.split('\n')[0]}"
                          </p>
                        )}
                        {randomReview.users?.full_name && (
                          <p className="text-gray-500 text-[10px] mt-0.5">
                            — {randomReview.users.full_name}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-[10px] mt-1">
                        From {displayName}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </a>

            {/* Support CTA — second */}
            <a
              href={`/${talent?.username || username}/support?skipMessage=true`}
              className="block"
            >
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl overflow-hidden border border-green-500/30 hover:border-green-500/50 transition-all duration-300 hover:scale-[1.02] p-5">
                <h3 className="text-white font-semibold text-base mb-2">Support {firstName} With a Donation</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-300 text-sm">
                    <span className="text-green-400">✓</span> Get a guaranteed response from {firstName}
                  </li>
                  <li className="flex items-center gap-2 text-gray-300 text-sm">
                    <span className="text-green-400">✓</span> Your message shown on {firstName}'s page
                  </li>
                  <li className="flex flex-col gap-1 text-gray-300 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-green-400">✓</span> Rank higher in {firstName}'s top fans list by supporting!
                    </div>
                    <div className="ml-5 mt-1 self-start inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      ⚡ Earn 1 point per $1 donated and show up higher on {firstName}'s Top Fans
                    </div>
                  </li>
                </ul>
              </div>
            </a>

            {/* Recent ShoutOuts Video Feed */}
            {recentShoutouts.length > 0 && (
              <div className="mt-6">
                <h3 className="text-white font-semibold text-sm mb-3">ShoutOuts {firstName} has sent other fans.</h3>
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory" style={{ scrollbarWidth: 'none' }}>
                  {recentShoutouts.map(shoutout => (
                    <div key={shoutout.id} className="flex-shrink-0 w-40 snap-start">
                      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                        <div className="aspect-[9/16] relative bg-black">
                          <video
                            src={`${shoutout.video_url}#t=0.5`}
                            className="w-full h-full object-cover"
                            playsInline
                            preload="auto"
                            onClick={(e) => {
                              const vid = e.currentTarget;
                              if (vid.paused) {
                                vid.muted = false;
                                vid.play();
                                setPlayingVideoId(shoutout.id);
                              } else {
                                vid.pause();
                                setPlayingVideoId(null);
                              }
                            }}
                            onEnded={() => setPlayingVideoId(null)}
                          />
                          {playingVideoId !== shoutout.id && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="w-10 h-10 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                                <PlayIcon className="h-5 w-5 text-white ml-0.5" />
                              </div>
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 p-2 pointer-events-none">
                            <p className="text-white text-xs font-medium truncate">For {shoutout.recipient_name || 'a fan'}</p>
                            <p className="text-gray-400 text-[10px]">{shoutout.occasion || 'ShoutOut'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="pt-6 flex items-center justify-center gap-3">
          <p className="text-gray-600 text-xs">Powered by ShoutOut</p>
          {email && (
            <button onClick={() => { localStorage.removeItem('bio_subscriber_email'); localStorage.removeItem('bio_account_verified'); window.location.reload(); }} className="text-gray-600 text-xs hover:text-gray-400 transition-colors">Logout</button>
          )}
        </div>
      </div>

      {/* OTP Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-white text-center mb-1">Verify Your Identity</h3>
            <p className="text-gray-400 text-sm text-center mb-5">Quick verification to send your message</p>
            {!otpSent && otpNeedsPhone ? (
              <div className="space-y-3">
                <input type="tel" value={otpPhone} onChange={e => setOtpPhone(formatPhoneInput(e.target.value))} placeholder="Phone number"
                  className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none text-center text-lg" />
                <p className="text-gray-500 text-xs text-center">{PHONE_HELP_TEXT}</p>
                <button onClick={handleOtpSend} disabled={otpLoading}
                  className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {otpLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : 'Send Code'}
                </button>
              </div>
            ) : otpSent ? (
              <div className="space-y-3">
                <p className="text-gray-400 text-sm text-center">Code sent to {otpPhoneHint || '***'}</p>
                <input type="text" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" maxLength={6} autoFocus
                  className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white text-center text-2xl tracking-[0.5em] placeholder-gray-500 focus:outline-none font-mono"
                  onKeyDown={e => { if (e.key === 'Enter') handleOtpVerify(); }} />
                <button onClick={handleOtpVerify} disabled={otpLoading}
                  className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {otpLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : 'Verify & Continue'}
                </button>
                <button onClick={() => { setOtpSent(false); setOtpCode(''); setOtpNeedsPhone(true); }}
                  className="w-full text-center text-sm text-gray-400 hover:text-white">Change number</button>
              </div>
            ) : (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
              </div>
            )}
            <button onClick={() => setShowOtpModal(false)}
              className="w-full mt-4 text-center text-sm text-gray-500 hover:text-white">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagePage;
